import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNull } from 'lodash';
import { CampaignContext } from '../../contexts/CampaignContext.tsx';
import { useLocation } from 'react-router-dom';

interface IEditPrice {
	title: string;
	open: boolean;
	onClose: () => void;
	type: string | undefined;
	id: number | null;
	isCreate: boolean;
}

const priceSchema = z.object({
	view: z.coerce.number({ required_error: 'view_count_is_required' }).min(2, 'view_count_must_be_at_least_2'),
	price: z.coerce.number({ required_error: 'price_is_required' }).min(4, 'Price must be at least 4'),
});

export default function EditPriceModal({ title, open, onClose, type, id, isCreate }: IEditPrice) {
	const { t } = useTranslation();
	const { fetchedMedias } = useContext(CampaignContext);
	const [data, setData] = useState<{ price: number; view: number }>();
	const [valMedia, setValMedia] = useState<any | null>(null);
	const { pathname } = useLocation();

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<z.infer<typeof priceSchema>>({
		resolver: zodResolver(priceSchema),
	});

	useEffect(() => {
		if (!isNull(id) && !isCreate) {
			axios({
				url: `/admin/campaign-view-price/${id}`,
				method: 'GET',
				loading: true,
			}).then(result => {
				if (result?.data?.success) {
					setValMedia(result.data.data.media_id);
					setValue('view', result?.data?.data.view);
					setValue('price', result?.data?.data.price);
					setData(result?.data?.data);
				}
			});
		}
	}, [id]);

	const handleSearchMedia = (event: SelectChangeEvent) => {
		setValMedia(event.target.value);
	};

	const handleNew = (data: z.infer<typeof priceSchema>) => {
		axios({
			url: '/admin/campaign-view-price',
			method: 'POST',
			data: { ...data, type: type, media_id: pathname.startsWith('/finance/media/view_campaign') ? valMedia : 9 },
			loading: true,
		}).then(response => {
			console.log('Data sent successfully:', response.data);
			onClose();
		});
	};

	const handleEdit = (data: z.infer<typeof priceSchema>) => {
		axios({
			url: `/admin/campaign-view-price/${id}`,
			method: 'PUT',
			data: { ...data, type: type, media_id: pathname.startsWith('/finance/media/view_campaign') ? valMedia : 9 },
			loading: true,
		}).then(response => {
			console.log('Data sent successfully:', response.data);
			onClose();
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent className="flex flex-col gap-3 py-4">
				<TextField label={t('view_count')} variant="outlined" {...register('view')} error={!!errors?.view} helperText={t(errors?.view?.message!)} defaultValue={!isCreate ? (data?.view ?? 0) : undefined} />
				<TextField label={t('view_price')} variant="outlined" {...register('price')} error={!!errors?.price} helperText={t(errors?.price?.message!)} defaultValue={!isCreate ? (data?.price ?? 0) : undefined} />
				{pathname.startsWith('/finance/media/view_campaign') ? (
					<FormControl fullWidth>
						<InputLabel id="demo-simple-select">{t('media')}</InputLabel>

						<Select labelId="demo-simple-select-label" id="demo-simple-select" value={valMedia ? valMedia : ''} onChange={handleSearchMedia}>
							{fetchedMedias?.map(res => (
								<MenuItem key={res.id} value={res.id}>
									{res.title}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				) : (
					''
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined">
					{t('cancel')}
				</Button>
				{isCreate ? (
					<Button onClick={handleSubmit(handleNew)} color="primary" type="submit">
						{t('new')}
					</Button>
				) : (
					<Button onClick={handleSubmit(handleEdit)} color="primary" type="submit">
						{t('edit')}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
